import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box } from '@honda-canada/design-system-react';
import { nodeData, nodesData } from '@lukeaus/plain-tree';
import { useSelectedOptions } from '../SelectProductsContext/SelectProductsProvider';
import themeStyles from './styles/DropDownSelectors.styles';
import DropDownSelector from './DropDownSelector';
import useSelectionFromUrl from './useSelectionFromUrl';

const DropdownWrapper = themeStyles.apply(Box, 'DropdownWrapper');

const DropdownSelectors = ({ productCatalogDataTree, dropdownSchema, setIsExpanded }) => {
  const history = useHistory();

  const { search, pathname } = (typeof window !== 'undefined' && window.location) || {};
  const params = new URLSearchParams(search);
  const paramsObj = Object.fromEntries(params);

  const { selectedOptions, setSelectedOptions } = useSelectedOptions();
  useSelectionFromUrl(paramsObj, productCatalogDataTree, setIsExpanded);

  const handleChange = useCallback(
    (value, nodes, index, dropdown) => {
      if (!value || !Array.isArray(nodes)) return;
      const targetNode = nodes.find(node => nodeData(node)?.value === value);
      const queryParams = new URLSearchParams(search);

      const param = dropdown?.key;
      const valueStr = targetNode?.data?.detIdentifier || targetNode?.data?.value;
      queryParams.set(param, valueStr);

      const newUrl = `${pathname}?${decodeURIComponent(queryParams.toString())}`;
      const selectedQueryParam = `${param}=${valueStr.replace(/ /g, '+')}`;

      // use current query param to update the url and clean up redundant selection on the url
      const updatedUrl = `${newUrl?.split(selectedQueryParam)?.[0]}${selectedQueryParam}`;
      history.replace(updatedUrl);
      setSelectedOptions({ targetNode, index });
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [dropdownSchema, setSelectedOptions],
  );

  if (!Array.isArray(dropdownSchema) || !dropdownSchema.length || !productCatalogDataTree) return null;

  const rootNode = productCatalogDataTree.root;

  return (
    <DropdownWrapper>
      {dropdownSchema.map((dropdown, index) => {
        const parentNode = index ? selectedOptions[index - 1] : rootNode;
        let options = [];
        let currentNodes = [];
        if (parentNode) {
          currentNodes = parentNode.children || [];
          options = nodesData(currentNodes) || [];
        }

        return (
          <DropDownSelector
            key={dropdown.key}
            dropdown={dropdown}
            options={options}
            parentNode={parentNode}
            currentNodes={currentNodes}
            index={index}
            handleChange={handleChange}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
          />
        );
      })}
    </DropdownWrapper>
  );
};

DropdownSelectors.propTypes = {
  productCatalogDataTree: PropTypes.shape({
    root: PropTypes.shape({}),
  }),
  dropdownSchema: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      placeholder: PropTypes.string,
    }),
  ),
  setIsExpanded: PropTypes.func,
};

export default React.memo(DropdownSelectors);
